import styled from "@emotion/styled";
import { text, surface, border as borderToken, interactive, zIndex, borderRadius, controlFontSize, fontSize, lineHeight, spacing, layout, shadow } from "@atlas-int/tokens";
import { ChevronUp, ChevronDown, DropdownToggleButton, DropdownItem } from "@atlas-int/ui";
import BaseCloseButton from "@atlas-int/ui/CloseButton";

export { ClearButtonSymbol } from "@atlas-int/ui/CloseButton";

// ─── Shared Arrow Icons (canonical chevrons from @atlas-int/ui) ─────────────

export const ArrowUp = ChevronUp;
export const ArrowDown = ChevronDown;

// ─── Config ──────────────────────────────────────────────────────────────────

export interface DropdownConfig {
  maxHeight?: string;
  menuBgColor?: string;
  menuBorderColor?: string;
  menuBorderRadius?: string;
  menuZIndex?: number;
  containerBorderColor?: string;
  containerRadius?: string;
  togglePosition?: "relative" | "absolute";
  toggleDisplay?: string;
  toggleHeight?: string;
  itemHeight?: string;
  itemPaddingRight?: string;
  itemOverflow?: string;
}

const defaults: Required<DropdownConfig> = {
  maxHeight: layout.menuMaxHeight,
  menuBgColor: surface.raised,
  menuBorderColor: borderToken.control,
  menuBorderRadius: borderRadius.base,
  menuZIndex: zIndex.dropdown,
  containerBorderColor: borderToken.control,
  containerRadius: borderRadius.base,
  togglePosition: "absolute",
  toggleDisplay: "inline-block",
  toggleHeight: "100%",
  itemHeight: spacing[16],
  itemPaddingRight: spacing[4],
  itemOverflow: "hidden",
};

function resolve(config: DropdownConfig = {}): Required<DropdownConfig> {
  return { ...defaults, ...config };
}

// ─── Factory Functions ───────────────────────────────────────────────────────

export function createMainMenu(config?: DropdownConfig) {
  const c = resolve(config);

  return styled.ul`
    max-height: ${c.maxHeight};
    overflow-y: auto;
    position: absolute;
    z-index: ${c.menuZIndex};
    background-color: ${c.menuBgColor};
    list-style-type: none;
    padding-left: 0px;
    padding: 0px;
    margin: 0px;
    width: 100%;
    margin-block: 0;
    box-sizing: border-box;
    box-shadow: ${shadow.sm};
    border: 1px solid ${c.menuBorderColor};
    border-radius: ${c.menuBorderRadius};
    margin-top: ${spacing[1]};

    & .menu-item--highlighted {
      background-color: ${surface.hover};
    }
  `;
}

export function createBaseMenuItem(config?: DropdownConfig) {
  const c = resolve(config);
  return styled.li`
    ${c.itemHeight !== "auto" ? `height: ${c.itemHeight};` : ""}
    cursor: pointer;
    width: calc(100% - 0px);
    display: flex;
    -webkit-box-align: center;
    align-items: center;
    font-size: ${fontSize.base};
    color: ${text.primary};
    box-sizing: border-box;
    position: relative;
    padding-right: ${c.itemPaddingRight};
    ${c.itemOverflow !== "visible" ? `overflow: ${c.itemOverflow};` : ""}

    &.menu-item--highlighted {
      background-color: ${surface.hover};
    }

    &.menu-item--selected {
      background-color: ${surface.hover};
    }
  `;
}

export function createDropdownContainer(config?: DropdownConfig) {
  const c = resolve(config);
  return styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    position: relative;
    height: ${layout.controlsHeight};
    line-height: ${lineHeight.compact};
    box-sizing: border-box;
    padding: 0px;
    border: 1px solid ${c.containerBorderColor};
    border-radius: ${c.containerRadius};
  `;
}

export function createToggleButton(config?: DropdownConfig) {
  const c = resolve(config);
  return styled(DropdownToggleButton)`
    position: ${c.togglePosition};
    ${c.togglePosition === "absolute" ? "right: 4px; top: 50%; transform: translateY(-50%);" : "display: " + c.toggleDisplay + ";"}
    margin: auto 0px;
    z-index: ${zIndex.dropdown};
  `;
}

// ─── Canonical ClearButton ──────────────────────────────────────────────────

export const ClearButton = styled(BaseCloseButton)`
  position: absolute;
  right: 25.6px;
  height: 100%;
  top: 0px;
  bottom: 0px;
  width: ${spacing[8]};
  color: ${interactive.muted};
`;
